/**
 * Recorte de listagem — fatiar, limitar a página, contar.
 *
 * O RECORTE é o conjunto que sobra depois dos filtros e da busca, e é a base de
 * tudo que a tela afirma: contagem, totais, exportação e seleção em lote. A
 * PÁGINA é só a fatia exibida.
 *
 * Este módulo existe porque o mesmo cálculo se repete em ~30 telas e porque
 * dois defeitos concretos JÁ aconteceram nesta base:
 *
 *   1. PÁGINA ALÉM DO FIM — a lista encolhe enquanto se trabalha nela (vincular
 *      uma rubrica a tira da fila de pendentes) e a página atual deixa de
 *      existir. A tabela fica vazia e parece falha de carregamento; o operador
 *      não tem como adivinhar que basta voltar uma página.
 *      (visto em /averbadora/motor-rubricas, 29/08/2026)
 *
 *   2. TOTAL QUE SOMA A PÁGINA — cabeçalho agregando as 25 linhas visíveis em
 *      vez do recorte inteiro. Não dá erro: dá um número menor e plausível.
 *      (mesma classe do "valor total" zerado da ADF — ver lib/valor-contrato.ts)
 *
 * Espalhar isso por 30 telas sem um ponto testável é multiplicar por 30 a
 * chance de errar.
 */

/** Padrão do produto — já é o que as telas que paginam usam hoje. */
export const POR_PAGINA_PADRAO = 25;
export const OPCOES_POR_PAGINA = [10, 25, 50, 100] as const;

export interface Recorte<T> {
  /** A fatia exibida. */
  itens: T[];
  /** Registros do RECORTE (filtrado), nunca da página nem da base. */
  total: number;
  /** Página efetiva, já limitada ao intervalo válido. */
  pagina: number;
  porPagina: number;
  totalPaginas: number;
}

/** Quantas páginas o recorte tem. Recorte vazio ainda é 1 página (a vazia). */
export function totalDePaginas(total: number, porPagina: number): number {
  if (porPagina <= 0) return 1;
  return Math.max(1, Math.ceil(total / porPagina));
}

/**
 * Limita a página pedida ao intervalo que existe.
 *
 * Limitar na LEITURA, e não corrigir depois por um efeito, é deliberado: o
 * efeito só roda depois da renderização, então existe um quadro em que a tela
 * mostra a tabela vazia antes de se consertar. O operador vê o defeito piscar.
 */
export function paginaValida(pagina: number, total: number, porPagina: number): number {
  const paginas = totalDePaginas(total, porPagina);
  if (!Number.isFinite(pagina)) return 1;
  return Math.min(Math.max(1, Math.trunc(pagina)), paginas);
}

/**
 * Recorte no CLIENTE: a tela tem todas as linhas e fatia.
 *
 * `linhas` já vem filtrado e ORDENADO — ordenar depois de fatiar faria a
 * primeira página não conter os primeiros registros (FR-017).
 */
export function recortar<T>(linhas: readonly T[], pagina: number, porPagina: number): Recorte<T> {
  const total = linhas.length;
  const pp = porPagina > 0 ? Math.trunc(porPagina) : POR_PAGINA_PADRAO;
  const p = paginaValida(pagina, total, pp);
  const inicio = (p - 1) * pp;
  return {
    itens: linhas.slice(inicio, inicio + pp) as T[],
    total,
    pagina: p,
    porPagina: pp,
    totalPaginas: totalDePaginas(total, pp),
  };
}

/**
 * Recorte no SERVIDOR: a página já veio fatiada, o total veio junto.
 *
 * Mesma forma de saída que `recortar` de propósito — é o que deixa a diferença
 * entre cliente e servidor invisível na tela (FR-020b). O componente de
 * paginação não sabe (nem precisa saber) de onde veio o recorte.
 */
export function recorteDoServidor<T>(
  itens: readonly T[],
  total: number,
  pagina: number,
  porPagina: number,
): Recorte<T> {
  const pp = porPagina > 0 ? Math.trunc(porPagina) : POR_PAGINA_PADRAO;
  const t = Math.max(0, Math.trunc(total));
  return {
    itens: [...itens] as T[],
    total: t,
    pagina: paginaValida(pagina, t, pp),
    porPagina: pp,
    totalPaginas: totalDePaginas(t, pp),
  };
}

/** Deslocamento a mandar para a API. Sempre derivado da página JÁ limitada. */
export function offsetDe(pagina: number, porPagina: number, total: number): number {
  const pp = porPagina > 0 ? Math.trunc(porPagina) : POR_PAGINA_PADRAO;
  return (paginaValida(pagina, total, pp) - 1) * pp;
}

// ============================================================ filtros

export type Predicado<T> = (linha: T) => boolean;

/**
 * Aplica filtros nomeados, opcionalmente ignorando UM deles.
 *
 * O `exceto` é a razão de esta função existir. A contagem exibida num chip
 * precisa responder "quantos eu veria se clicasse aqui", e para isso ela sai da
 * base com todos os OUTROS filtros aplicados, menos o da própria dimensão
 * (FR-010). Contagem calculada sobre a base inteira mente assim que qualquer
 * outro filtro está ligado — e o operador só descobre depois de clicar.
 *
 * Filtro `null`/`undefined` = dimensão sem corte; é ignorado.
 */
export function aplicarFiltros<T>(
  linhas: readonly T[],
  filtros: Record<string, Predicado<T> | null | undefined>,
  exceto?: string,
): T[] {
  const ativos = Object.entries(filtros).filter(
    ([nome, p]) => typeof p === "function" && nome !== exceto,
  ) as [string, Predicado<T>][];
  if (ativos.length === 0) return [...linhas] as T[];
  return linhas.filter((l) => ativos.every(([, p]) => p(l))) as T[];
}

/**
 * Contagem por valor de uma dimensão.
 *
 * Passe como `linhas` o resultado de `aplicarFiltros(..., exceto: <dimensão>)`
 * — é isso que torna o número honesto.
 */
export function contagens<T>(linhas: readonly T[], valorDe: (linha: T) => string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const l of linhas) {
    const v = valorDe(l);
    out[v] = (out[v] ?? 0) + 1;
  }
  return out;
}

/**
 * Por que a lista está vazia. A distinção é FR-013.
 *
 * "Nada bate com o filtro" lido como "não há dados" faz o operador procurar no
 * lugar errado — ou concluir que a importação falhou.
 */
export type MotivoVazio = "sem-dados" | "filtro-vazio" | null;

export function motivoVazio(totalDaBase: number, totalDoRecorte: number): MotivoVazio {
  if (totalDoRecorte > 0) return null;
  return totalDaBase === 0 ? "sem-dados" : "filtro-vazio";
}
